import { useState } from "react";
import { Sidebar, type TabGroup } from "./components/Sidebar";
import { AppStateProvider } from "./hooks/useAppState";
import { useConnection } from "./hooks/useConnection";
import { TabCreateJob } from "./components/tabs/TabCreateJob";
import { TabDeadLetters } from "./components/tabs/TabDeadLetters";
import { TabExamples } from "./components/tabs/TabExamples";
import { TabGeocode } from "./components/tabs/TabGeocode";
import { TabHealth } from "./components/tabs/TabHealth";
import { TabJobActions } from "./components/tabs/TabJobActions";
import { TabJobResult } from "./components/tabs/TabJobResult";
import { TabJobStatus } from "./components/tabs/TabJobStatus";
import { TabMap } from "./components/tabs/TabMap";
import { TabMapVector } from "./components/tabs/TabMapVector";
import { TabMatrix } from "./components/tabs/TabMatrix";
import { TabOverview } from "./components/tabs/TabOverview";
import { TabValidate } from "./components/tabs/TabValidate";

const groups: TabGroup[] = [
  {
    title: "Começo",
    items: [
      {
        id: "overview",
        label: "Visão geral",
        description: "Caminho recomendado de uso",
      },
      {
        id: "examples",
        label: "Exemplos prontos",
        description: "Catálogo de JSONs (../examples)",
      },
    ],
  },
  {
    title: "Otimização",
    items: [
      {
        id: "validate",
        label: "Validar payload",
        description: "POST /validate (não persiste)",
        badge: "POST",
      },
      {
        id: "create-job",
        label: "Criar job",
        description: "POST /jobs com Idempotency-Key",
        badge: "POST",
      },
      {
        id: "job-status",
        label: "Status do job",
        description: "GET com polling automático",
        badge: "GET",
      },
      {
        id: "job-result",
        label: "Resultado",
        description: "Rotas, ETAs e métricas do solver",
        badge: "GET",
      },
      {
        id: "job-actions",
        label: "Cancelar / Retry",
        description: "Operações de ciclo de vida",
      },
    ],
  },
  {
    title: "Geo",
    items: [
      {
        id: "matrix",
        label: "Matriz O/D",
        description: "POST /matrix (OSRM)",
        badge: "OSRM",
      },
      {
        id: "geocode",
        label: "Geocoding",
        description: "POST /geocode + /reverse-geocode",
        badge: "OSM",
      },
      {
        id: "map",
        label: "Mapa",
        description: "Rotas no TileServer GL",
        badge: "GL",
      },
      {
        id: "map-vector",
        label: "Mapa (vetorial)",
        description: "MapLibre GL + tiles vetoriais",
        badge: "MVT",
      },
    ],
  },
  {
    title: "Operação",
    items: [
      {
        id: "dead-letters",
        label: "Dead Letters",
        description: "Jobs falhos com replay/discard",
        badge: "DLQ",
      },
      {
        id: "health",
        label: "Probes",
        description: "/health e /ready do upstream",
      },
    ],
  },
];

export function App() {
  const [activeId, setActiveId] = useState<string>("overview");
  const connection = useConnection();

  return (
    <AppStateProvider>
      <div className="flex h-full">
        <Sidebar
          groups={groups}
          activeId={activeId}
          onChange={setActiveId}
          connection={connection}
        />
        <main className="ks-scroll flex-1 overflow-auto bg-ink-50/60">
          <div className="mx-auto max-w-7xl px-6 py-6">
            <TabRouter activeId={activeId} onNavigate={setActiveId} connection={connection} />
          </div>
        </main>
      </div>
    </AppStateProvider>
  );
}

function TabRouter({
  activeId,
  onNavigate,
  connection,
}: {
  activeId: string;
  onNavigate: (id: string) => void;
  connection: ReturnType<typeof useConnection>;
}) {
  switch (activeId) {
    case "overview":
      return <TabOverview connection={connection} onJump={onNavigate} />;
    case "examples":
      return <TabExamples onNavigate={onNavigate} />;
    case "validate":
      return <TabValidate />;
    case "create-job":
      return <TabCreateJob onNavigate={onNavigate} />;
    case "job-status":
      return <TabJobStatus onNavigate={onNavigate} />;
    case "job-result":
      return <TabJobResult onNavigate={onNavigate} />;
    case "job-actions":
      return <TabJobActions />;
    case "matrix":
      return <TabMatrix />;
    case "geocode":
      return <TabGeocode onNavigate={onNavigate} />;
    case "map":
      return <TabMap />;
    case "map-vector":
      return <TabMapVector />;
    case "dead-letters":
      return <TabDeadLetters onNavigate={onNavigate} />;
    case "health":
      return <TabHealth />;
    default:
      return <TabOverview connection={connection} onJump={onNavigate} />;
  }
}
